import React from "react";
import { Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CustomCleaveInput from "@/components/CustomCleaveInput/CustomCleaveInput";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FilePickerDialog from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import ThumbnailDisplay from "@/modules/hub/features/FileManagement/components/ThumbnailDisplay/ThumbnailDisplay";
import { ProductStoreProduct } from "../../api/fetchProductStoreProducts/types";
import { CreateProductStoreProductRequest } from "../../api/createProductStoreProduct/types";
import { useProductForm } from "../../hooks/useProductForm";

interface ProductFormProps {
  initialData?: ProductStoreProduct | null;
  onSubmit: (data: CreateProductStoreProductRequest) => Promise<void>;
  isLoading: boolean;
}

const ProductForm: React.FC<ProductFormProps> = ({
  initialData,
  onSubmit,
  isLoading,
}) => {
  const {
    form,
    isEditing,
    categories,
    loadingCategories,
    thumbnailDisplayUrl,
    thumbnailFileName,
    isThumbnailPickerOpen,
    openThumbnailPicker,
    closeThumbnailPicker,
    handleThumbnailSelected,
    handleRemoveThumbnail,
    submit,
  } = useProductForm({ initialData, onSubmit });
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <form className="space-y-6" onSubmit={submit}>
      <div className="space-y-4">
        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="code"
          >
            Product Code *
          </label>
          <Input
            className="dark:bg-gray-800"
            id="code"
            placeholder="e.g., PROD-001"
            {...register("code")}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            A unique code to identify this product (letters, numbers, hyphens,
            underscores).
          </p>
          {errors.code && (
            <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="name"
          >
            Product Name *
          </label>
          <Input
            className="dark:bg-gray-800"
            id="name"
            placeholder="Enter product name"
            {...register("name")}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="description"
          >
            Description *
          </label>
          <Textarea
            className="dark:bg-gray-800"
            id="description"
            placeholder="Describe the product..."
            rows={4}
            {...register("description")}
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">
              {errors.description.message}
            </p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="price"
          >
            Price ($) *
          </label>
          <Controller
            control={control}
            name="price"
            render={({ field }) => (
              <CustomCleaveInput
                className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm dark:bg-gray-800 dark:border-slate-700 dark:text-slate-100"
                inputMode="decimal"
                onChange={(rawValue) => {
                  field.onChange(rawValue === "" ? 0 : parseFloat(rawValue));
                }}
                options={{}}
                value={String(field.value ?? "")}
              />
            )}
          />
          {errors.price && (
            <p className="text-red-500 text-sm mt-1">{errors.price.message}</p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="type"
          >
            Product Type *
          </label>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="dark:bg-gray-800">
                  <SelectValue placeholder="Select product type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical">Physical</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Physical products require shipping; virtual products are delivered
            digitally.
          </p>
          {errors.type && (
            <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="categoryId"
          >
            Category *
          </label>
          <Controller
            control={control}
            name="categoryId"
            render={({ field }) => (
              <Select
                disabled={loadingCategories}
                onValueChange={field.onChange}
                value={field.value}
              >
                <SelectTrigger className="dark:bg-gray-800">
                  <SelectValue
                    placeholder={
                      loadingCategories
                        ? "Loading categories..."
                        : "Select a category"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.categoryId && (
            <p className="text-red-500 text-sm mt-1">
              {errors.categoryId.message}
            </p>
          )}
        </div>

        <div className="[&>div]:max-w-none [&>div>button]:dark:bg-gray-800">
          <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
            Thumbnail
          </label>
          <ThumbnailDisplay
            onRemove={handleRemoveThumbnail}
            onSelect={openThumbnailPicker}
            thumbnailFileName={thumbnailFileName}
            thumbnailUrl={thumbnailDisplayUrl || null}
          />
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <label
                className="block text-sm font-medium text-gray-900 dark:text-white"
                htmlFor="isPublished"
              >
                Published
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                When published, the product will be visible in the storefront.
              </p>
            </div>
            <Controller
              control={control}
              name="isPublished"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  id="isPublished"
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button
          disabled={isLoading}
          onClick={() => window.history.back()}
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button disabled={isLoading || loadingCategories} type="submit">
          {isLoading
            ? "Saving..."
            : isEditing
              ? "Save Changes"
              : "Create Product"}
        </Button>
      </div>
      <FilePickerDialog
        allowedFileTypes={[
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ]}
        isOpen={isThumbnailPickerOpen}
        multiSelect={false}
        namespace="product_store"
        onClose={closeThumbnailPicker}
        onSelect={handleThumbnailSelected}
        title="Select Thumbnail"
      />
    </form>
  );
};

export default ProductForm;
